import React, { useState, useMemo, useEffect } from 'react';
import { BiSort } from 'react-icons/bi';
import SearchBar from '@components/Layout/SearchBar';
import Pagination from '@components/Layout/Pagination';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { CREATE_DEVICE_PATH, MANAGEMENT_ASSIGN_PATH } from '@constants/routes';
import { useDispatch } from 'react-redux';
import { FaFilter } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import { IoMdArrowRoundBack } from 'react-icons/io';
import DeviceManagementTable from '@components/Layout/DeviceManagementTable';
import DownIcon from '@components/SvgComponents/DownIcon';
import UpIcon from '@components/SvgComponents/UpIcon';
import { getUserAttributes } from '@utils/authUtils';
import { TENANT_ID } from '@constants/cognitoAttributes';
import { deviceList } from '@utils/utils';
import { useAppSelector } from '@store/hooks';
import DataLoader from '@components/DataLoader';
import {
    hospitalTableUuid,
    hospitalUuid,
    nurseDeviceList,
} from '@utils/hospitalUtils';
import AssignedDeviceTable from '@components/Layout/AssignedDeviceTable';
import Select, { SingleValue, MultiValue } from 'react-select';
import {
    PutItemCommand,
    PutItemCommandInput,
    QueryCommand,
} from '@aws-sdk/client-dynamodb';
import { dbClient } from '@utils/awsClients';
import { v4 as uuidv4 } from 'uuid';
import Swal from 'sweetalert2';
import ErrorMessage from '@components/Error/ErrorMessage';
import AssignModalPopUp from '@components/AssignModalPopUp';

const MySwal = Swal;

const parseDate = (dateString: string): Date => {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
};

type CustomDropdownToggleProps = {
    children?: React.ReactNode;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const CustomDropdownToggle = React.forwardRef<
    HTMLButtonElement,
    CustomDropdownToggleProps
>(({ children, onClick }, ref) => (
    <button
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            onClick?.(e);
        }}
        className="btn text-white py-2 bg-primary"
    >
        {children}
        <FaFilter className="ml-2" /> {/* Add your custom icon here */}
    </button>
));

interface NurseDevice {
    id: string;
    device_uuid: string;
    device_name: string;
    device_id: string;
    nurse_name: string;
    nurse_id: string;
    nurse_uuid: string;
    created_at: string;
    created_at_time: string;
    hospital_id: string;
    has_control?: boolean;
    controlling_nurse_name?: string;
}

interface GroupedDeviceData {
    device_uuid: string;
    device_name: string;
    device_id: string;
    controlling_nurse_name?: string;
    hospital_id: string;
    created_at_time: string;
    nurses: NurseDevice[];
}

interface Nurse {
    tenant_id: string;
    user_id: string;
    name: string;
    uuid: string;
    nurse_id?: string;
}

interface Device {
    uuid: string;
    device_id: string;
    device_name?: string;
    hospital_id: string;
}

const AssignManagementList = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('newest');
    const [sort, setSort] = useState('newest');
    const [filter, setFilter] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filterField, setFilterField] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [nurseData, setNurseData] = useState<Nurse[]>([]);
    const [deviceData, setDeviceData] = useState<Device[]>([]);
    const [deviceDetails, setDeviceDetails] = useState<GroupedDeviceData[]>([]);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [isSortExpanded, setIsSortExpanded] = useState(true);
    const [isInfusionExpanded, setIsInfusionExpanded] = useState(true);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const loggedInUserTenantID = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );
    const fetchAttributes = async () => {
        try {
            const res = await hospitalTableUuid(loggedInUserTenantID);

            if (res && res.uuid?.S) {
                const uuid = res.uuid?.S;
                const data = await nurseDeviceList(uuid);
                setDeviceDetails(data);
                // await fetchData();
            }
        } catch (error: any) {
            console.log('err', error);
        } finally {
            setLoading(false);
        }
        // }
    };

    const fetchData = async () => {
        try {
            const data = await hospitalUuid(loggedInUserTenantID);
            if (data?.nurseList && data?.deviceList) {
                setNurseData(data?.nurseList);
                setDeviceData(data?.deviceList);
            }
            // setUserData(data);
        } catch (error: any) {
            let errorMessage = 'Unexpected error';

            switch (error.name) {
                case 'ResourceNotFoundException':
                    errorMessage = 'Error: Table or item not found.';
                    break;
                case 'InternalServerError':
                    errorMessage = 'Error: Internal server error.';
                    break;
                case 'ProvisionedThroughputExceededException':
                    errorMessage =
                        'Error: Provisioned throughput exceeded. Please try again later.';
                    break;
                case 'LimitExceededException':
                    errorMessage =
                        'Error: Limit exceeded. Please try again later.';
                    break;
                default:
                    errorMessage = error.message;
                    break;
            }
            MySwal.fire({
                position: 'top-end', // Position the toast in the top-right corner
                icon: 'error',
                title: errorMessage,
                showConfirmButton: false,
                timer: 3000,
                toast: true,
                customClass: {
                    popup: '!text-red !bg-light_red', // Add custom class to the popup
                },
                width: 'auto',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAttributes();
    }, []);

    const [tableHeading, setTableHeading] = useState([
        {
            id: 1,
            title: t('management.assign.device_name'),
        },
        {
            id: 2,
            title: t('management.assign.device_id'),
        },
        {
            id: 3,
            title: t('management.assign.controlled_by'),
        },
        {
            id: 4,
            title: t('management.assign.assigned_date'),
        },
        {
            id: 5,
            title: t('management.assign.action'),
        },
        // Add more user objects here
    ]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleSortChange = (event: string) => {
        setSort(event);
    };

    const handleFilterChange = (value: string) => {
        setFilter(value);
        // setStatusFilter(value);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setRowsPerPage(Number(event.target.value));
        setCurrentPage(1); // Reset to first page
    };
    const handleCloseModal = (event: any) => {
        event.stopPropagation();
        setIsDropdownVisible(false);
    };
    const handleAddDevice = () => {
        navigate(CREATE_DEVICE_PATH);
    };
    const backArrow = () => {
        navigate(-1);
    };

    const toggleSortExpand = () => {
        setIsSortExpanded(!isSortExpanded);
    };
    const toggleInfusionExpand = () => {
        setIsInfusionExpanded(!isInfusionExpanded);
    };

    const filteredUsers = useMemo(() => {
        let filtered = deviceDetails ?? [];

        if (searchTerm) {
            filtered = filtered.filter(
                (data) =>
                    data.device_id.includes(searchTerm.toLowerCase()) ||
                    data.controlling_nurse_name
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
            );
        }

        return filtered;
    }, [searchTerm, filterField, statusFilter, deviceDetails]);

    const sortedData = useMemo(() => {
        let sorted = [...filteredUsers];
        if (sortField !== undefined && sortField !== null) {
            sorted.sort((a: any, b: any) => {
                const dateA = new Date(a.created_at_time);
                const dateB = new Date(b.created_at_time);

                if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                    if (sortField.toLowerCase() == 'newest') {
                        return dateB.getTime() - dateA.getTime();
                    } else if (sortField.toLowerCase() == 'oldest') {
                        return dateA.getTime() - dateB.getTime();
                    }
                }

                // Handle cases where one or both dates are invalid
                if (isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                    return sortField.toLowerCase() == 'newest' ? 1 : -1; // Treat invalid dateA as older/newer
                }
                if (!isNaN(dateA.getTime()) && isNaN(dateB.getTime())) {
                    return sortField.toLowerCase() == 'newest' ? -1 : 1; // Treat invalid dateB as older/newer
                }

                return 0;
            });
        }

        return sorted;
    }, [filteredUsers, sortField]);

    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, currentPage, rowsPerPage]);

    const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
    const hasPaginatedUsers = paginatedUsers.length > 0;

    const handleClear = (event: any) => {
        event.stopPropagation();
        setSortField('newest');
        setSort('newest');
        setFilterField('');
        setFilter('');
        handleCloseModal(event);
    };

    const handleSave = (event: any) => {
        event.stopPropagation();
        setSortField(sort);
        setFilterField(filter);
        handleCloseModal(event);
    };

    const handleAssigned = () => {
        navigate(MANAGEMENT_ASSIGN_PATH);
    };

    const handleDropdownToggle = (event: any) => {
        event.stopPropagation();
        // Prevent the default behavior to keep the dropdown open
        if (event && event.target) {
            // Check if the event target has the data-click-type attribute
            const isButtonClick = event.target.getAttribute('data-click-type');

            if (
                isButtonClick === 'btn-clear' ||
                isButtonClick === 'btn-save' ||
                isButtonClick === 'btn-close'
            ) {
                setIsDropdownVisible(false);
            } else {
                // setIsDropdownVisible(true);
                setIsDropdownVisible(!isDropdownVisible);
            }
        }
    };

    async function assign() {
        try {
            setLoading(true);
            await fetchData();
        } catch (error: any) {
            console.log('err', error);
        } finally {
            setShowModal(true);
            setLoading(false);
        }
    }

    return (
        <>
            <div className="2xl:max-w-full">
                <div className="flex align-items-center justify-content-between">
                    <div
                        className="flex items-center cursor-pointer"
                        // onClick={backArrow}
                    >
                        {/* <IoMdArrowRoundBack className="w-8 h-8" /> */}
                        <h1 className="text-lg font-semibold ml-2">
                            {t('management.assign.title')}
                        </h1>
                    </div>
                    <button
                        className="bg-primary rounded py-2 px-4 btn text-white ml-4"
                        onClick={assign}
                    >
                        {/* <span className="mr-3">+</span> */}
                        {t('management.assign.assign_btn')}
                    </button>
                </div>
                <div className="flex align-items-center justify-content-between my-4">
                    <SearchBar
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                    />
                    <div className="">
                        <Dropdown
                            show={isDropdownVisible}
                            onClick={(event) => handleDropdownToggle(event)}
                            data-click-type="btn-open"
                        >
                            <Dropdown.Toggle
                                as={CustomDropdownToggle}
                                variant="primary"
                                id="dropdown-basic"
                            >
                                {t('user_management.hospital_user.filter')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu onClick={(e) => e.stopPropagation()}>
                                <span className="ml-4 font-bold text-black text-sm">
                                    {t('management.device.filter')}
                                </span>
                                <button
                                    className="absolute right-0 top-0.5 cursor-pointer"
                                    onClick={(event) => handleCloseModal(event)}
                                    data-click-type="btn-close"
                                >
                                    <span className="absolute text-xs top-1.5 right-0 mr-3 cursor-pointer flex items-center">
                                        {t('management.device.close')}
                                        <IoClose className="ml-1" />
                                    </span>
                                </button>
                                <Dropdown.Header
                                    className="font-bold text-black  text-sm d-flex justify-content-between align-items-center"
                                    onClick={toggleSortExpand}
                                >
                                    {t('management.device.sort_bt')}
                                    {isSortExpanded ? <DownIcon /> : <UpIcon />}
                                </Dropdown.Header>
                                {isSortExpanded && (
                                    <>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleSortChange('newest')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="newest"
                                                value="newest"
                                                aria-label="newest"
                                                checked={
                                                    sort.toLowerCase() ==
                                                    'newest'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t(
                                                'user_management.individual_user.newest'
                                            )}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleSortChange('oldest')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="oldest"
                                                value="oldest"
                                                aria-label="oldest"
                                                checked={
                                                    sort.toLowerCase() ==
                                                    'oldest'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t(
                                                'user_management.individual_user.oldest'
                                            )}
                                        </Dropdown.Item>
                                    </>
                                )}

                                <div className="flex cursor-pointer align-items-center justify-content-center space-x-4 px-4 py-2">
                                    <button
                                        className="bg-white border-2 border-primary  text-primary px-4 py-1 rounded"
                                        onClick={(event) => handleClear(event)}
                                        data-click-type="btn-clear"
                                    >
                                        {t('management.device.clear')}
                                    </button>
                                    <button
                                        className="text-white bg-primary rounded px-4 py-1"
                                        onClick={(event) => handleSave(event)}
                                        data-click-type="btn-save"
                                    >
                                        {t('management.device.apply')}
                                    </button>
                                </div>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
                <AssignedDeviceTable
                    data={paginatedUsers}
                    header={tableHeading}
                    fetchAttributes={fetchAttributes}
                    totalData={deviceDetails}
                    row={rowsPerPage}
                />
                {paginatedUsers.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={setRowsPerPage}
                        handlePageChange={setCurrentPage}
                        disablePrevious={!hasPaginatedUsers}
                        disableNext={!hasPaginatedUsers}
                    />
                )}
            </div>
            {loading && <DataLoader />}
            {showModal && (
                <>
                    <AssignModalPopUp
                        showModal={showModal}
                        deviceData={deviceData}
                        nurseData={nurseData}
                        onClose={() => setShowModal(false)}
                        refreshData={() => fetchAttributes()}
                    />
                </>
            )}
        </>
    );
};

export default AssignManagementList;
